import { useState } from "react";
import { useNavigate } from "react-router";
import { BottomCTA, TopNavigation } from "@hamlak/ui";
import { apiFetch, isApiError } from "@/api/client";
import { ep } from "@/api/endpoints";
import type { ProfileCreateRequest, ProfileCreateResponse } from "@/api/types";
import { analytics } from "@/analytics";
import { signupContext } from "@/analytics/signupContext";
import { t } from "@/i18n/ko";
import { useAuthStore } from "@/stores/authStore";
import { useUiStore } from "@/stores/uiStore";
import { ProfileForm, type ProfileFormValue } from "./ProfileForm";
import { WelcomeDialog } from "./WelcomeDialog";
import { shouldOfferPushAsk } from "@/features/push/push";
import { OnboardingCloseButton } from "./OnboardingCloseButton";
import "./auth.css";

/**
 * F01-05 대화 프로필(이름·성별·소개; 계정 사진은 /me/edit, spec 7절) + F01-06 웰컴.
 * POST /api/me/profiles → {profile, welcome} → 웰컴 다이얼로그(수치는 서버) → (앱·알림 미질문이면 F01-15) → returnTo ?? /home.
 */
export function ProfileNewPage() {
  const navigate = useNavigate();
  const setFlags = useAuthStore((s) => s.setFlags);
  const setBalance = useAuthStore((s) => s.setBalance);
  const showSnackbar = useUiStore((s) => s.showSnackbar);
  const [form, setForm] = useState<ProfileFormValue>({ name: "", bio: "", gender: "none" });
  const { name, bio, gender } = form;
  const [nameError, setNameError] = useState<string | undefined>();
  const [pending, setPending] = useState(false);
  const [welcome, setWelcome] = useState<ProfileCreateResponse["welcome"]>(null);

  const canSubmit = name.trim().length > 0 && !pending;

  // ⑨ 웰컴 다음: 앱이고 OS 알림 권한을 아직 묻지 않았으면 F01-15(returnTo 는 그 화면이 소비), 아니면 바로 returnTo ?? /home (AC0-260)
  const finish = async () => {
    if (await shouldOfferPushAsk().catch(() => false)) {
      navigate("/push-permission", { replace: true });
      return;
    }
    const ui = useUiStore.getState();
    const next = ui.returnTo ?? "/home";
    ui.setReturnTo(null);
    navigate(next, { replace: true });
  };

  const submit = async () => {
    if (!name.trim()) {
      setNameError(t("profile.name.required"));
      return;
    }
    setPending(true);
    try {
      const body: ProfileCreateRequest = { name: name.trim(), gender, isDefault: true, ...(bio.trim() ? { bio: bio.trim() } : {}), signupContext: signupContext() };
      const res = await apiFetch<ProfileCreateResponse>(ep.meProfiles, { method: "POST", body });
      setFlags({ profileRequired: false });
      // 대표 프로필 요약 캐시도 바로 — 플롯 캐시 키(usePlotViewer)가 다음 GET /me 전에 이름으로 바뀐다 (AC0-359)
      useAuthStore.setState({ profile: { id: res.profile.id, name: res.profile.name, gender: res.profile.gender } });
      // 첫 프로필 = 가입 확정(서버 complete_signup). 이때부터 user_id 로 잇는다
      const userId = useAuthStore.getState().user?.id;
      if (userId) analytics.setUserId(userId);
      if (res.welcome) {
        setBalance(res.welcome.balance);
        setWelcome(res.welcome);
      } else {
        await finish();
      }
    } catch (e) {
      showSnackbar(isApiError(e) && e.code === "NETWORK" ? t("error.network") : t("error.generic"));
    } finally {
      setPending(false);
    }
  };

  return (
    <main className="page auth profile">
      <TopNavigation title={t("page.profileNew")} left={null} right={<OnboardingCloseButton />} />
      <div className="auth__form">
        <ProfileForm
          value={form}
          onChange={(v) => {
            setForm(v);
            setNameError(undefined);
          }}
          errors={{ name: nameError }}
          showDefaultNotice
        />
      </div>
      <BottomCTA primary={{ children: t("profile.cta"), disabled: !canSubmit, loading: pending, onClick: () => void submit() }} />
      <WelcomeDialog welcome={welcome} onConfirm={() => void finish()} />
    </main>
  );
}
